import React, { useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import makeRequest from '@llt-frontend/make-request';
import { AnalyticsEventTrigger } from '@llt-frontend/react-analytics-context';
import { Button } from '@llt-frontend/react-material-button';
import { CircularProgress } from '@llt-frontend/react-material-progress';

import GradeLevelSelect from './grade-level-select';
import PackageTiles from './package-tiles';
import SubjectSelect from './subject-select';
import useAppContext from 'scripts/hooks/context/use-app-context';

// Constants
const ANALYTICS_CATEGORY = 'tutoring-packages';
const GRADE_COOKIE_KEY = 'selected_grade_level';
const SUBJECT_COOKIE_KEY = 'selected_subject';

const useFetchPackages = ({ clientId, gradeLevelId, subjectId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [packages, setPackages] = useState();
  const [promotion, setPromotion] = useState();

  const { analyticsContext } = useAppContext();

  const fetchPackages = async () => {
    setPackages(undefined);
    setError(false);
    setIsLoading(true);
    const response = await makeRequest(
      `${process.env.REACT_APP_API_HOST}/v4/clients/${clientId}/available_packages?grade_level_id=${gradeLevelId}&subject_id=${subjectId}&limit=4`
    );

    if (!response || !response.packages) {
      analyticsContext.trackEvent({
        action: 'no-packages-found',
        category: ANALYTICS_CATEGORY,
        label: `grade-${gradeLevelId}-subject-id-${subjectId}`,
      });

      setError(true);
      setIsLoading(false);
      return;
    }

    analyticsContext.trackEvent({
      action: 'row-packages-loaded',
      category: ANALYTICS_CATEGORY,
    });

    setPackages(response.packages);
    setPromotion(response.promotions?.[0]);
    setIsLoading(false);
  };

  const resetPackages = () => {
    setPackages(undefined);
    setPromotion(undefined);
    setIsLoading(false);
    setError(false);
  };

  return { isLoading, error, packages, promotion, fetchPackages, resetPackages };
};

export const PackageSelector = ({ clientId }) => {
  const [cookies, setCookie] = useCookies();

  const initialSubject = cookies[SUBJECT_COOKIE_KEY] ? cookies[SUBJECT_COOKIE_KEY] : undefined;
  const [subjectId, setSubjectId] = useState(initialSubject?.value);

  const initialGradeLevel = cookies[GRADE_COOKIE_KEY] ? cookies[GRADE_COOKIE_KEY] : undefined;
  const [gradeLevelId, setGradeLevelId] = useState(
    initialGradeLevel ? parseInt(initialGradeLevel, 10) : undefined
  );

  const { t, ready: translationLoaded } = useTranslation('self-service/packages/selector');

  const {
    fetchPackages,
    resetPackages,
    packages,
    promotion,
    error: packageError,
    isLoading: isPackagesLoading,
  } = useFetchPackages({ clientId, subjectId, gradeLevelId });

  function handleGradeSelect(value) {
    setGradeLevelId(parseInt(value, 10));
    setCookie(GRADE_COOKIE_KEY, value, {
      domain: window.location.hostname,
      path: '/',
    });
    resetPackages();
  }

  function handleSubjectSelect(value, primaryText) {
    setSubjectId(value);
    setCookie(
      SUBJECT_COOKIE_KEY,
      { value, primaryText },
      {
        domain: window.location.hostname,
        path: '/',
      }
    );
    resetPackages();
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!clientId || !gradeLevelId || !subjectId) {
      return;
    }

    await fetchPackages();
  }

  if (!translationLoaded) {
    return (
      <div className="package-selector__loading">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="package-selector">
      <h5 className="package-selector__label">{t('headline')}</h5>
      <form onSubmit={handleSubmit}>
        <div className="package-selector__select-container">
          <GradeLevelSelect savedValue={initialGradeLevel} onChange={handleGradeSelect} />
          <SubjectSelect
            initialValue={initialSubject ? initialSubject.primaryText : undefined}
            onChange={handleSubjectSelect}
          />
        </div>
        <div className="package-selector__button-container">
          <AnalyticsEventTrigger
            analytics={{
              action: 'view-packages',
              category: ANALYTICS_CATEGORY,
              label: `grade-${gradeLevelId}-subject-id-${subjectId}`,
            }}
            element={Button}
            elementProps={{
              className: 'package-selector__button',
              contained: true,
              type: 'submit',
            }}
          >
            {t('viewPackages')}
          </AnalyticsEventTrigger>
        </div>
      </form>
      {packageError && <p className="package-selector__error centered">{t('loadPackagesError')}</p>}
      <PackageTiles loading={isPackagesLoading} packages={packages} promotion={promotion} />
    </div>
  );
};
